import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { invoke } from '@tauri-apps/api/core';
import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { DESKTOP_AUTH_HANDOFF_EVENT } from 'hooks/use-desktop-deep-link-handler';
import { useTranslate } from 'hooks/use-translation';
import { logger } from 'services/logger';
const Page = styled.div`
  min-height: 100vh;
  background: #fff;
  padding: 48px 56px;
  font-family:
    'Inter',
    -apple-system,
    BlinkMacSystemFont,
    'Segoe UI',
    sans-serif;
  box-sizing: border-box;
`;
const Brand = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 48px;
`;
const BrandLogo = styled.img`
  display: block;
  height: 28px;
  width: auto;
`;
const Title = styled.h1`
  margin: 0 0 28px;
  font-size: 32px;
  font-weight: 600;
  color: #374151;
  letter-spacing: -0.02em;
`;
const Form = styled.form`
  max-width: 360px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;
const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: 8px;
  font-size: 14px;
  font-weight: 500;
  color: #374151;
`;
const InputWrap = styled.div`
  position: relative;
`;
const Input = styled.input`
  width: 100%;
  box-sizing: border-box;
  height: 40px;
  padding: 0 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  color: #111827;
  background: #fff;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15);
  }

  &:disabled {
    background: #f9fafb;
  }
`;
// Leaves room for the show/hide toggle that overlays the field.
const PasswordInput = styled(Input)`
  padding-right: 40px;
`;
const PasswordToggle = styled.button`
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  border: none;
  background: transparent;
  color: #6b7280;
  cursor: pointer;
  padding: 4px;
  font-size: 14px;
`;
const Submit = styled.button`
  margin-top: 4px;
  height: 40px;
  border: none;
  border-radius: 6px;
  background: #3b82f6;
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: #2563eb;
  }

  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
  }
`;
const MagicLink = styled.button`
  border: none;
  background: transparent;
  color: #3b82f6;
  font-size: 14px;
  cursor: pointer;
  padding: 0;
  text-align: left;
  align-self: flex-start;

  &:hover:not(:disabled) {
    text-decoration: underline;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;
const Divider = styled.hr`
  border: none;
  border-top: 1px solid #e5e7eb;
  margin: 8px 0 0;
`;
const Footer = styled.p`
  margin: 16px 0 0;
  font-size: 14px;
  color: #4b5563;

  a {
    color: #3b82f6;
    text-decoration: none;
    font-weight: 500;
  }

  a:hover {
    text-decoration: underline;
  }
`;
const Message = styled.p`
  margin: 0;
  font-size: 13px;
  color: ${({ $tone })=>$tone === 'error' ? '#b91c1c' : '#047857'};
`;
export const DesktopLogin = ()=>{
    const translate = useTranslate();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    useEffect(()=>{
        const onHandoff = (event)=>{
            const detail = event.detail;
            if (!detail) {
                return;
            }
            if (detail.status === 'pending') {
                setError(null);
                setSuccess(translate(detail.messageKey, detail.messageValues));
                setSubmitting(true);
                return;
            }
            if (detail.status === 'success') {
                setError(null);
                setSuccess(translate('desktop.login.signed_in'));
                setSubmitting(true);
                return;
            }
            setSuccess(null);
            setError(translate(detail.messageKey, detail.messageValues));
            setSubmitting(false);
        };
        window.addEventListener(DESKTOP_AUTH_HANDOFF_EVENT, onHandoff);
        return ()=>{
            window.removeEventListener(DESKTOP_AUTH_HANDOFF_EVENT, onHandoff);
        };
    }, [
        translate
    ]);
    const onSubmit = async (event)=>{
        event.preventDefault();
        setError(null);
        setSuccess(null);
        setSubmitting(true);
        try {
            const result = await invoke('desktop_signin', {
                userName: email.trim(),
                password
            });
            if (result.status >= 200 && result.status < 300) {
                setSuccess(translate('desktop.login.email.sent'));
                return;
            }
            logger.warn('desktop signin error', {
                status: result.status
            });
            setError(translate('desktop.login.signin.failed', {
                status: result.status
            }));
        } catch (err) {
            logger.error('desktop signin exception', {
                error: err
            });
            setError(translate('desktop.login.signin.error'));
        } finally{
            setSubmitting(false);
        }
    };
    const onMagicLink = async ()=>{
        setError(null);
        setSuccess(null);
        const trimmed = email.trim();
        if (!trimmed) {
            setError(translate('desktop.login.magic_link.no_email'));
            return;
        }
        setSubmitting(true);
        try {
            const result = await invoke('desktop_request_magic_link', {
                emailId: trimmed
            });
            if (result.status >= 200 && result.status < 300) {
                setSuccess(translate('desktop.login.email.sent'));
                return;
            }
            logger.warn('desktop magic link error', {
                status: result.status
            });
            setError(translate('desktop.login.magic_link.failed', {
                status: result.status
            }));
        } catch (err) {
            logger.error('desktop magic link exception', {
                error: err
            });
            setError(translate('desktop.login.magic_link.error'));
        } finally{
            setSubmitting(false);
        }
    };
    return /*#__PURE__*/ _jsxs(Page, {
        "data-cy": "desktop-login",
        children: [
            /*#__PURE__*/ _jsx(Brand, {
                children: /*#__PURE__*/ _jsx(BrandLogo, {
                    src: "https://cdn.prod.website-files.com/6360d3edc696260ba2aecbc7/637615eae7b1c63dd172c518_logo.svg",
                    alt: translate('rocketlane')
                })
            }),
            /*#__PURE__*/ _jsx(Title, {
                children: translate('desktop.login.title')
            }),
            /*#__PURE__*/ _jsxs(Form, {
                onSubmit: onSubmit,
                children: [
                    /*#__PURE__*/ _jsxs(Field, {
                        children: [
                            translate('email'),
                            /*#__PURE__*/ _jsx(Input, {
                                "data-cy": "desktop-login-email",
                                type: "email",
                                autoComplete: "username",
                                value: email,
                                onChange: (e)=>setEmail(e.target.value),
                                required: true,
                                disabled: submitting
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs(Field, {
                        children: [
                            translate('password'),
                            /*#__PURE__*/ _jsxs(InputWrap, {
                                children: [
                                    /*#__PURE__*/ _jsx(PasswordInput, {
                                        "data-cy": "desktop-login-password",
                                        type: showPassword ? 'text' : 'password',
                                        autoComplete: "current-password",
                                        value: password,
                                        onChange: (e)=>setPassword(e.target.value),
                                        required: true,
                                        disabled: submitting
                                    }),
                                    /*#__PURE__*/ _jsx(PasswordToggle, {
                                        type: "button",
                                        "aria-label": showPassword ? translate('desktop.login.password.hide.aria') : translate('desktop.login.password.show.aria'),
                                        onClick: ()=>setShowPassword((v)=>!v),
                                        children: showPassword ? translate('desktop.login.password.hide') : translate('show')
                                    })
                                ]
                            })
                        ]
                    }),
                    error ? /*#__PURE__*/ _jsx(Message, {
                        $tone: "error",
                        "data-cy": "desktop-login-error",
                        children: error
                    }) : null,
                    success ? /*#__PURE__*/ _jsx(Message, {
                        $tone: "success",
                        "data-cy": "desktop-login-success",
                        children: success
                    }) : null,
                    /*#__PURE__*/ _jsx(Submit, {
                        "data-cy": "desktop-login-submit",
                        type: "submit",
                        disabled: submitting,
                        children: submitting ? translate('desktop.login.submit.loading') : translate('login')
                    }),
                    /*#__PURE__*/ _jsx(MagicLink, {
                        type: "button",
                        "data-cy": "desktop-login-magic-link",
                        disabled: submitting,
                        onClick: onMagicLink,
                        children: translate('desktop.login.magic_link.prompt')
                    }),
                    /*#__PURE__*/ _jsx(Divider, {}),
                    /*#__PURE__*/ _jsxs(Footer, {
                        children: [
                            translate('desktop.login.no_account'),
                            ' ',
                            /*#__PURE__*/ _jsx("a", {
                                href: "https://www.rocketlane.com/",
                                target: "_blank",
                                rel: "noreferrer",
                                children: translate('desktop.login.sign_up')
                            })
                        ]
                    })
                ]
            })
        ]
    });
};
